import { apiFetch } from './index.js';

/**
 * Obtener todos los métodos de pago
 */
export const obtenerMetodosPago = async () => {
  try {
    const response = await apiFetch('/api/MetodoPago');
    
    return {
      success: true,
      data: response || [],
      count: Array.isArray(response) ? response.length : 0
    };
    
  } catch (error) {
    console.error('Error obteniendo métodos de pago:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener métodos de pago activos
 */
export const obtenerMetodosPagoActivos = async () => {
  try {
    const response = await apiFetch('/api/MetodoPago/activos');
    
    return {
      success: true,
      data: response || [],
      count: Array.isArray(response) ? response.length : 0
    };
    
  } catch (error) {
    console.error('Error obteniendo métodos de pago activos:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener método de pago por ID
 */
export const obtenerMetodoPagoPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de método de pago es requerido');
    
    const response = await apiFetch(`/api/MetodoPago/${id}`);
    
    return {
      success: true,
      data: response,
      message: 'Método de pago obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`Error obteniendo método de pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener método de pago con relaciones (pagos asociados)
 */
export const obtenerMetodoPagoConRelaciones = async (id) => {
  try {
    if (!id) throw new Error('ID de método de pago es requerido');
    
    const response = await apiFetch(`/api/MetodoPago/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response,
      message: 'Método de pago con relaciones obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`Error obteniendo método de pago con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener estadísticas de un método de pago
 */
export const obtenerEstadisticasMetodoPago = async (id) => {
  try {
    if (!id) throw new Error('ID de método de pago es requerido');
    
    const response = await apiFetch(`/api/MetodoPago/${id}/estadisticas`);
    
    return {
      success: true,
      data: response,
      message: 'Estadísticas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`Error obteniendo estadísticas ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Crear un nuevo método de pago
 */
export const crearMetodoPago = async (metodoData) => {
  try {
    console.log('➕ Creando nuevo método de pago...');
    
    // Validar datos básicos
    const requiredFields = ['nombre', 'tipo'];
    const missingFields = requiredFields.filter(field => !metodoData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Campos requeridos faltantes: ${missingFields.join(', ')}`);
    }
    
    // Validar que tipo sea válido según tu backend
    const tiposValidos = ['efectivo', 'transferencia', 'pago_movil', 'tarjeta', 
                          'transferencia_internacional', 'cripto', 'aplicacion_credito'];
    
    if (!tiposValidos.includes(metodoData.tipo)) {
      throw new Error(`Tipo inválido. Debe ser uno de: ${tiposValidos.join(', ')}`);
    }
    
    // Preparar los datos según lo que espera el backend
    const metodoParaEnviar = {
      nombre: String(metodoData.nombre),
      descripcion: metodoData.descripcion || null,
      tipo: String(metodoData.tipo),
      campos_requeridos: metodoData.campos_requeridos || null,
      acepta_dolares: metodoData.acepta_dolares ?? true,
      acepta_bolivares: metodoData.acepta_bolivares ?? true,
      acepta_usdt: metodoData.acepta_usdt ?? false,
      estado: metodoData.estado || "Activo"
    };
    
    console.log('📤 Enviando método de pago:', metodoParaEnviar);
    
    const response = await apiFetch('/api/MetodoPago', {
      method: 'POST',
      body: metodoParaEnviar
    });
    
    console.log('✅ Respuesta:', response);
    
    return {
      success: true,
      data: response,
      message: 'Método de pago creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando método de pago:', error);
    
    let errorMessage = error.message;
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      errorMessage = 'Error de conexión. Verifica que el servidor esté corriendo.';
    } else if (error.message.includes('500')) {
      errorMessage = 'Error interno del servidor.';
    }
    
    return {
      success: false,
      error: errorMessage,
      data: null
    };
  }
};

/**
 * Actualizar método de pago
 */
export const actualizarMetodoPago = async (id, metodoData) => {
  try {
    console.log('✏️ Actualizando método de pago...');
    
    if (!id) throw new Error('ID de método de pago es requerido');
    
    // Preparar los datos según lo que espera el backend
    const metodoParaEnviar = {
      nombre: String(metodoData.nombre),
      descripcion: metodoData.descripcion || null,
      tipo: String(metodoData.tipo),
      campos_requeridos: metodoData.campos_requeridos || null,
      acepta_dolares: metodoData.acepta_dolares ?? true,
      acepta_bolivares: metodoData.acepta_bolivares ?? true,
      acepta_usdt: metodoData.acepta_usdt ?? false,
      estado: metodoData.estado || "Activo"
    };
    
    console.log('📤 Actualizando método de pago:', metodoParaEnviar);
    
    const response = await apiFetch(`/api/MetodoPago/${id}`, {
      method: 'PUT',
      body: metodoParaEnviar
    });
    
    console.log('✅ Respuesta:', response);
    
    return {
      success: true,
      data: response,
      message: 'Método de pago actualizado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error actualizando método de pago:', error);
    
    let errorMessage = error.message;
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      errorMessage = 'Error de conexión. Verifica que el servidor esté corriendo.';
    } else if (error.message.includes('500')) {
      errorMessage = 'Error interno del servidor.';
    }
    
    return {
      success: false,
      error: errorMessage,
      data: null
    };
  }
};

/**
 * Cambiar estado de método de pago (Activo/Inactivo)
 */
export const cambiarEstadoMetodoPago = async (id, estado) => {
  try {
    console.log('🔄 Cambiando estado de método de pago...');
    
    if (!id) throw new Error('ID de método de pago es requerido');
    if (!estado) throw new Error('Estado es requerido');
    
    const response = await apiFetch('/api/MetodoPago/cambiar-estado', {
      method: 'PATCH',
      body: { 
        idMetodoPago: Number(id),
        estado: String(estado) 
      }
    });
    
    console.log('✅ Respuesta:', response);
    
    return {
      success: true,
      data: response,
      message: 'Estado actualizado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error cambiando estado de método de pago:', error);
    
    let errorMessage = error.message;
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      errorMessage = 'Error de conexión. Verifica que el servidor esté corriendo.';
    } else if (error.message.includes('500')) {
      errorMessage = 'Error interno del servidor.';
    }
    
    return {
      success: false,
      error: errorMessage,
      data: null
    };
  }
};

/**
 * Eliminar (desactivar) método de pago
 */
export const eliminarMetodoPago = async (id) => {
  try {
    console.log('🗑️ Eliminando método de pago...');
    
    if (!id) throw new Error('ID de método de pago es requerido');
    
    await apiFetch(`/api/MetodoPago/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      message: 'Método de pago eliminado/desactivado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error eliminando método de pago:', error);
    
    let errorMessage = error.message;
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      errorMessage = 'Error de conexión. Verifica que el servidor esté corriendo.';
    } else if (error.message.includes('500')) {
      errorMessage = 'Error interno del servidor.';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Verificar si un método de pago existe
 */
export const verificarMetodoPagoExiste = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    
    const response = await apiFetch(`/api/MetodoPago/${id}/exists`);
    
    return {
      success: true,
      data: response,
      message: 'Verificación completada'
    };
    
  } catch (error) {
    console.error('Error verificando existencia:', error);
    return {
      success: false,
      error: error.message,
      data: false
    };
  }
};

/**
 * Validar si un nombre ya existe
 */
export const validarNombreMetodoPago = async (nombre, idExcluir = null) => {
  try {
    if (!nombre) throw new Error('Nombre es requerido');
    
    const params = new URLSearchParams();
    params.append('nombre', nombre);
    if (idExcluir) {
      params.append('idExcluir', idExcluir);
    }
    
    const response = await apiFetch(`/api/MetodoPago/validar-nombre?${params.toString()}`);
    
    return {
      success: true,
      data: response,
      message: 'Validación completada'
    };
    
  } catch (error) {
    console.error('Error validando nombre:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener métodos de pago por tipo
 */
export const obtenerMetodosPagoPorTipo = async (tipo) => {
  try {
    if (!tipo) throw new Error('Tipo es requerido');
    
    const response = await apiFetch(`/api/MetodoPago/tipo/${tipo}`);
    
    return {
      success: true,
      data: response || [],
      count: Array.isArray(response) ? response.length : 0
    };
    
  } catch (error) {
    console.error(`Error obteniendo métodos de pago por tipo ${tipo}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Filtrar métodos de pago por moneda
 */
export const filtrarMetodosPagoPorMoneda = async (filtros) => {
  try {
    // Preparar filtros según lo que espera el backend
    const filtrosParaEnviar = {
      acepta_dolares: filtros.acepta_dolares ?? null,
      acepta_bolivares: filtros.acepta_bolivares ?? null,
      acepta_usdt: filtros.acepta_usdt ?? null
    };
    
    const response = await apiFetch('/api/MetodoPago/filtrar-por-moneda', {
      method: 'POST',
      body: filtrosParaEnviar
    });
    
    return {
      success: true,
      data: response || [],
      count: Array.isArray(response) ? response.length : 0
    };
    
  } catch (error) {
    console.error('Error filtrando métodos de pago por moneda:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Serializar campos requeridos a JSON
 */
export const serializarCamposRequeridos = async (campos) => {
  try {
    const response = await apiFetch('/api/MetodoPago/serializar-campos', {
      method: 'POST',
      body: campos
    });
    
    return {
      success: true,
      data: response,
      message: 'Campos serializados exitosamente'
    };
    
  } catch (error) {
    console.error('Error serializando campos:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Deserializar campos requeridos desde JSON
 */
export const deserializarCamposRequeridos = async (jsonString) => {
  try {
    const response = await apiFetch('/api/MetodoPago/deserializar-campos', {
      method: 'POST',
      body: { jsonString }
    });
    
    return {
      success: true,
      data: response,
      message: 'Campos deserializados exitosamente'
    };
    
  } catch (error) {
    console.error('Error deserializando campos:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener tipos de métodos de pago disponibles
 */
export const obtenerTiposDisponibles = async () => {
  try {
    const response = await apiFetch('/api/MetodoPago/tipos-disponibles');
    
    return {
      success: true,
      data: response || [],
      count: Array.isArray(response) ? response.length : 0
    };
    
  } catch (error) {
    console.error('Error obteniendo tipos disponibles:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

export default {
  obtenerMetodosPago,
  obtenerMetodosPagoActivos,
  obtenerMetodoPagoPorId,
  obtenerMetodoPagoConRelaciones,
  obtenerEstadisticasMetodoPago,
  crearMetodoPago,
  actualizarMetodoPago,
  cambiarEstadoMetodoPago,
  eliminarMetodoPago,
  verificarMetodoPagoExiste,
  validarNombreMetodoPago,
  obtenerMetodosPagoPorTipo,
  filtrarMetodosPagoPorMoneda,
  serializarCamposRequeridos,
  deserializarCamposRequeridos,
  obtenerTiposDisponibles
};